import React, { useEffect, useState } from 'react';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactJSON from 'shared/design-system/components/TrueImpactJSON';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactDebug from 'npo/pages/ReportBuilder/TrueImpactDebug';
import AnswersList from 'npo/pages/ReportBuilder/Components/AnswersModal/AnswersList';
import { useReportAnswers } from 'shared/apollo/pageHooks/useReportAnswers';
import { Answer } from 'npo/pages/ReportBuilder/Components/Review/utils';
import { ReportBuilderData } from 'shared/providers/ReportProvider/utils';
import { ReportsTable } from '../ReportGroup';

interface SnapshotDebugProps {
  report: ReportsTable;
  questions?: unknown;
  isAllComplete?: boolean;
  activeQuestionId?: number | null;
  activeQuestion?: unknown;
}

/**
 * Inner loader — only mounts once debug mode is turned on, so the
 * `useReportAnswers` query does not fire on initial render. Answers are
 * fetched exactly once (network-only) and cached via the local `answers`
 * state; the user can trigger a manual refresh with the button in the
 * modal header.
 */
interface DebugAnswersProps {
  report: ReportsTable;
  open: boolean;
  onClose: () => void;
  extra?: Record<string, unknown>;
}

const DebugAnswers: React.FC<DebugAnswersProps> = ({
  report,
  open,
  onClose,
  extra,
}) => {
  const {
    data: reportAnswers,
    refetch: reloadAnswers,
    loading: loadingAnswers,
  } = useReportAnswers(report.dbId);
  const [answers, setAnswers] = useState<Answer[]>(reportAnswers as Answer[]);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!loadingAnswers) {
      setAnswers((reportAnswers as Answer[]) || []);
      setHasLoaded(true);
    }
  }, [loadingAnswers, reportAnswers]);

  if (!open) return null;

  return (
    <TrueImpactModal
      open={open}
      hideFooter={true}
      showExpandButton={true}
      onClose={onClose}
      maxWidth="1100px"
      reload={{
        text: 'Refresh',
        onClick: () => reloadAnswers(),
        isLoading: !hasLoaded || loadingAnswers,
      }}
    >
      <div style={{ padding: SPACERS.small, fontSize: '12px' }}>
        <TrueImpactJSON parseObject={{ report, ...(extra || {}) }} />
      </div>
      {!hasLoaded && <TrueImpactLoading />}
      {hasLoaded && (
        <AnswersList
          report={(report as unknown) as ReportBuilderData}
          answers={answers || []}
          programName={report.program?.name || report.name}
          isReportPassed={false}
          setAnswers={setAnswers}
        />
      )}
    </TrueImpactModal>
  );
};

/**
 * Debug badge + answers modal for the Impact Snapshot editor. The answers
 * fetch is deferred until debug mode is toggled on (Ctrl/Cmd+Alt+D), and
 * the response is kept in local state so the query is not re-run on
 * subsequent modal opens.
 */
const SnapshotDebug: React.FC<SnapshotDebugProps> = ({
  report,
  questions,
  isAllComplete,
  activeQuestionId,
  activeQuestion,
}) => {
  const [isDebug, setIsDebug] = useState<boolean>(false);
  const [openAnswersModal, setOpenAnswersModal] = useState<boolean>(false);

  return (
    <>
      <TrueImpactDebug
        isDebug={isDebug}
        handleChange={setIsDebug}
        onClick={() => setOpenAnswersModal(true)}
      />
      {isDebug && (
        <DebugAnswers
          report={report}
          open={openAnswersModal}
          onClose={() => setOpenAnswersModal(false)}
          extra={{
            questions,
            isAllComplete,
            activeQuestionId,
            activeQuestion,
          }}
        />
      )}
    </>
  );
};

export default SnapshotDebug;
